import { useMemo, useState } from "react";

import { NO_CHECKS } from "./buildStatuses";
import { api, type ApiError } from "./client";
import type { ApiTask, ApiTaskDetail, ApiTaskStatus } from "./types";
import { useResource } from "./useResource";
import { APP_NAME } from "../../lib/appName";
import { relTime } from "../../lib/time";
import { waitingOnAgents } from "../../lib/waitingOnAgents";
import { useRealtimeScope } from "../realtime/useRealtimeScope";
import type { Task, TaskStatus } from "../types";

// Server task statuses collapse onto the board's five columns. Terminal
// statuses share the Done column, but `failed` keeps its own identity: it used
// to map to `done` outright, so a task that never even provisioned was reported
// to the user as finished work.
const STATUS_MAP: Record<ApiTaskStatus, TaskStatus> = {
  cancelled: "done",
  done: "done",
  failed: "failed",
  needs_input: "needs-input",
  pending: "pending",
  review: "review",
  running: "running",
};

/**
 * The board column a server status belongs in.
 *
 * The map is total over the statuses this client knows about, which is why the
 * lookup types as always finding one. `status` is whatever the response put
 * there, though, so one added server-side lands on "pending" rather than
 * `undefined`.
 */
export const toTaskStatus = (status: ApiTaskStatus): TaskStatus =>
  // eslint-disable-next-line tinsmith/no-unnecessary-nullish-coalescing
  STATUS_MAP[status] ?? "pending";

/** Server statuses after which nothing further happens on its own. */
const TERMINAL_API_STATUS = new Set<ApiTaskStatus>([
  "cancelled",
  "done",
  "failed",
]);

const num = (value: number | string | null): number => {
  if (value == null) {
    return 0;
  }
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// `agent_state` is the sandbox phase and is not cleared when a task ends — a
// task that failed while provisioning keeps `provisioning` forever. A
// finished task is not provisioning anything regardless of what the phase
// says, so the terminal status wins. Shared by the board card and the task
// header, which both gate UI on whether the sandbox is actually up yet.
const isProvisioning = (
  status: ApiTaskStatus,
  agentState: string | null,
): boolean =>
  !TERMINAL_API_STATUS.has(status) &&
  (agentState === "provisioning" || agentState === "booting");

/** Sandbox statuses that still have a container behind them. */
const LIVE_SANDBOX = new Set(["booting", "provisioning", "running"]);

// Whether this task's sandbox has been torn down — which is what archiving
// does (`agents-and-tasks.md` §Archiving is what releases the sandbox), and
// what every control that needs a container has to follow. A task not yet
// placed reports null and is *not* released: it has a sandbox coming, and the
// header says "starting…" for that.
const isSandboxReleased = (sandboxStatus: string | null | undefined): boolean =>
  sandboxStatus != null && !LIVE_SANDBOX.has(sandboxStatus);

// Map a server task row into the board's view model. Fields the board doesn't
// read (messages, services, pr, inputPrompt) get inert defaults; the task
// detail view populates them from dedicated endpoints.
export const mapTask = (task: ApiTask, repoId: string): Task => {
  const activityMs = task.last_activity_at
    ? Date.parse(task.last_activity_at)
    : Date.now();
  return {
    add: num(task.lines_added),
    base: task.base_branch,
    branch: task.target_branch,
    canceled: task.status === "cancelled",
    // Absent on a row from a server that predates the rollup, and on nothing
    // else — the board renders "no checks" rather than guessing at one.
    checks: task.checks_summary ?? NO_CHECKS,
    // The board sorts columns on this, so it has to be a timestamp that never
    // moves — activity time would reshuffle the cards on every agent turn.
    createdAt: task.created_at ? Date.parse(task.created_at) : activityMs,
    displayId: task.display_id,
    files: num(task.files_changed),
    id: task.public_id,
    inputPrompt: null,
    messages: [],
    pr: null,
    provisioning: isProvisioning(task.status, task.agent_state),
    rem: num(task.lines_removed),
    repo: task.repo_public_id ?? repoId,
    services: [],
    status: toTaskStatus(task.status),
    stepBudget: 0,
    stepsDone: 0,
    time: relTime(activityMs),
    title: task.title,
    waitingOnAgents: waitingOnAgents(task.status, task.waiting_on_agents),
    who: task.created_by_name ?? APP_NAME,
  };
};

export type UseRepoTasks = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly tasks: ReadonlyArray<Task>;
};

export const useRepoTasks = (
  repoId: string | null,
  archived: boolean,
): UseRepoTasks => {
  const { data, error, loading, refetch } = useResource<ReadonlyArray<ApiTask>>(
    repoId ? "/tasks" : null,
    repoId
      ? { archived: archived || undefined, limit: 200, repository_id: repoId }
      : undefined,
  );
  const tasks = useMemo(
    () => (data ?? []).map((t) => mapTask(t, repoId ?? "")),
    [data, repoId],
  );
  return { error, loading, refetch, tasks };
};

export type UseActivityTasks = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly repoNames: ReadonlyMap<string, string>;
  readonly tasks: ReadonlyArray<Task>;
};

// Cross-repo task feed for the Activity board (GET /tasks with no
// repository_id). Also derives a repo public_id → "org/name" map for the
// per-card repo chip.
export const useActivityTasks = (): UseActivityTasks => {
  const { data, error, loading, refetch } = useResource<ReadonlyArray<ApiTask>>(
    "/tasks",
    { limit: 200 },
  );
  const tasks = useMemo(
    () => (data ?? []).map((t) => mapTask(t, t.repo_public_id ?? "")),
    [data],
  );
  const repoNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of data ?? []) {
      if (t.repo_public_id) {
        map.set(
          t.repo_public_id,
          `${t.repo_org_slug ?? ""}/${t.repo_name ?? ""}`,
        );
      }
    }
    return map;
  }, [data]);
  return { error, loading, refetch, repoNames, tasks };
};

// Header view model for the task page (independent of the board `Task`).
export type TaskDetail = {
  readonly archived: boolean;
  readonly base: string;
  readonly branch: string | null;
  /**
   * The task failed before its agent was ever handed the prompt, so starting
   * it over loses nothing (`agents-and-tasks.md` §Retrying a task that never
   * started). The server makes the final call.
   */
  readonly canRetryStart: boolean;
  readonly displayId: string;
  readonly id: string;
  /**
   * `last_activity_at` as epoch ms, advancing on every turn. The tab badge
   * (`chrome/useFaviconAttention.ts`) re-arms off it: two turns in a row can
   * end on the same status, and a backgrounded tab never renders the `running`
   * between them, so the status value alone cannot tell the second stop apart
   * from nothing having happened.
   */
  readonly lastActivityAt: number;
  /**
   * Whether this task's sandbox can be previewed at all — false for every
   * ordinary task (`on-demand-services.md` §Non-goal).
   */
  readonly previewsDisabled: boolean;
  /** Whether the sandbox has a daemon connected yet — see `isProvisioning`. */
  readonly provisioning: boolean;
  readonly rawStatus: ApiTaskStatus;
  /**
   * Whether this task's sandbox has been torn down. Nothing that needs a
   * container — a shell, a process, a preview — is offered once it has, because
   * there is no container left to carry it out.
   */
  readonly sandboxReleased: boolean;
  readonly status: TaskStatus;
  readonly title: string;
  /** Children a parent in Review is waiting on; see `waitingOnAgents`. */
  readonly waitingOnAgents: number;
  /** The workspace's public id — what the composer's uploads are scoped to. */
  readonly workspaceId: string;
};

export const mapTaskDetail = (task: ApiTaskDetail): TaskDetail => ({
  archived: task.archived_at !== null,
  base: task.base_branch,
  branch: task.target_branch,
  canRetryStart:
    task.status === "failed" &&
    task.started_at === null &&
    task.archived_at === null,
  displayId: task.display_id,
  id: task.public_id,
  lastActivityAt: task.last_activity_at ? Date.parse(task.last_activity_at) : 0,
  previewsDisabled: task.previews_disabled === true,
  provisioning: isProvisioning(task.status, task.agent_state),
  rawStatus: task.status,
  sandboxReleased: isSandboxReleased(task.sandbox_status),
  status: toTaskStatus(task.status),
  title: task.title,
  waitingOnAgents: waitingOnAgents(task.status, task.waiting_on_agents),
  workspaceId: task.workspace_id,
});

export type UseTask = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly task: TaskDetail | undefined;
};

/**
 * The task a `/repos/{repo}/tasks/{T-441}` URL names.
 *
 * Keyed on the repository and the label rather than on an id, because the label
 * is what the URL carries and it counts from 1 in every workspace — it names a
 * task only alongside something that names the workspace, and the repository
 * does. This is the one call that takes a label; everything downstream is keyed
 * on `task.id`, the ULID it resolves to.
 */
export const useTask = (
  repoId: string | null,
  displayId: string | null,
): UseTask => {
  const { data, error, loading, refetch } = useResource<ApiTaskDetail>(
    repoId && displayId
      ? `/repositories/${repoId}/tasks/${encodeURIComponent(displayId)}`
      : null,
  );
  // Keep the open task view's header/status live: the agent transitioning the
  // task (needs_input/done/failed), a background PR merge/sync, or a teammate's
  // rename/archive all fan out `task.updated` on the task scope. The scope is
  // the resolved id, so it only opens once the task above has loaded.
  const publicId = data?.public_id ?? null;
  useRealtimeScope(publicId ? `task:${publicId}` : null, (event) => {
    if (event.kind === "task.updated" || event.kind === "resync") {
      refetch();
    }
  });
  const task = useMemo(() => (data ? mapTaskDetail(data) : undefined), [data]);
  // A parent waiting on its children hears nothing on its own scope when one
  // of them stops — a child ending its turn tells the parent nothing
  // (task-messages.md §What the parent is told) — so while it waits, another
  // task's status change in the repository is what keeps "Waiting on N
  // agents" honest. Only a status moves the count.
  const waiting = (task?.waitingOnAgents ?? 0) > 0;
  useRealtimeScope(waiting && repoId ? `repo:${repoId}` : null, (event) => {
    if (
      event.kind === "task.updated" &&
      (event.summary as { status?: unknown } | undefined)?.status !==
        undefined &&
      (event.ref as { public_id?: string } | undefined)?.public_id !== publicId
    ) {
      refetch();
    }
  });
  return { error, loading, refetch, task };
};

/**
 * One piece of work the agent has in flight (`agents-and-tasks.md` §What the
 * agent is running). Fields are the daemon's, unmapped — nothing between the
 * Agent SDK and the panel renames one.
 */
export type ApiAgentTask = {
  /** The subagent's type (`Explore`) or the workflow's name. A shell has none. */
  readonly agent?: string;
  /** Running detached: it outlives the turn that started it. */
  readonly backgrounded: boolean;
  readonly description: string;
  readonly id: string;
  /** `subagent`, `shell`, `local_workflow`, … */
  readonly kind: string;
  readonly lastTool?: string;
  readonly startedAt: string;
  readonly toolUses?: number;
};

/**
 * What the agent is running, live.
 *
 * The fetch is the baseline for a tab that arrives mid-turn; `agent.tasks.updated`
 * carries every set after it, so a subagent starting shows without a round trip.
 * Frames win until the next fetch, which is what a `resync` triggers — that is
 * the one case where the held set may be arbitrarily stale, since the event is
 * transient and nothing replays it.
 *
 * The held set is **tagged with the task it arrived for**, and which one is
 * current is *derived* rather than cleared in an effect — the same rule
 * {@link useTaskDiff} follows for a live frame, and for a sharper reason here.
 * The task page is not remounted when the route moves from one task to the
 * next, so a held set would otherwise outlive the task it describes: a fetch
 * corrects itself in a moment, but nothing ever corrects a frame for a task
 * whose agent is idle, and there is no second frame coming.
 */
export const useAgentTasks = (
  taskId: string | null,
): ReadonlyArray<ApiAgentTask> => {
  const { data, refetch } = useResource<{
    tasks: ReadonlyArray<ApiAgentTask>;
  }>(taskId ? `/tasks/${taskId}/agent-tasks` : null);
  const [live, setLive] = useState<{
    taskId: string;
    tasks: ReadonlyArray<ApiAgentTask>;
  } | null>(null);
  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (!taskId) {
      return;
    }
    if (event.kind === "agent.tasks.updated") {
      const { tasks } = event.summary as { tasks: ReadonlyArray<ApiAgentTask> };
      setLive({ taskId, tasks });
    } else if (event.kind === "resync") {
      setLive(null);
      refetch();
    }
  });
  if (live?.taskId === taskId) {
    return live.tasks;
  }
  return data?.tasks ?? [];
};

export type CreateTaskInput = {
  /**
   * Pending uploads to claim onto the opening prompt (`api.md` §Chat
   * attachments) — the New Agent composer's files.
   */
  readonly attachmentIds?: ReadonlyArray<string>;
  readonly effort?: string;
  readonly model?: string;
  readonly prompt: string;
  readonly repositoryId: string;
  readonly workspaceId: string;
};

export type CreatedTask = {
  readonly task: {
    /** `T-441` — what the task URL is keyed on. */
    readonly display_id: string;
    readonly public_id: string;
    readonly status: string;
    readonly title: string;
  };
};

// Create a task (POST /tasks). The server mints a ULID `public_id`, ratchets a
// `T-<n>` label out of the workspace's own counter, seeds the chat, and
// enqueues a sandbox-provision intent. Returns the new task.
export const createTask = (input: CreateTaskInput): Promise<CreatedTask> =>
  api.post<CreatedTask>("/tasks", {
    ...(input.attachmentIds?.length
      ? { attachment_ids: input.attachmentIds }
      : {}),
    // No `base_branch`: nothing chooses one at launch, so the server resolves
    // it from the repo's settings (`agents-and-tasks.md` §New agent screen).
    effort: input.effort,
    model: input.model,
    prompt: input.prompt,
    repository_id: input.repositoryId,
    workspace_id: input.workspaceId,
  });

// Intent-backed mutations. They enqueue server-side work; the caller should
// refetch (or await a realtime update, once wired) to observe the result.
export const cancelTask = (publicId: string): Promise<unknown> =>
  api.post(`/tasks/${publicId}/cancel`);

// Synchronous, not intent-backed (see cancelTask above): stops the agent's
// active turn, or cancels an open needs-input request, without ending the task.
export const interruptTask = (publicId: string): Promise<unknown> =>
  api.post(`/tasks/${publicId}/interrupt`);

// Starts over a task whose sandbox never came up: back to `pending` with a new
// provision intent (`agents-and-tasks.md` §Retrying a task that never started).
export const retryTaskStart = (publicId: string): Promise<unknown> =>
  api.post(`/tasks/${publicId}/retry`);

// One-way: archiving tears the sandbox down, so there is no route back
// (`agents-and-tasks.md` §Archive).
export const archiveTask = (publicId: string): Promise<unknown> =>
  api.post(`/tasks/${publicId}/archive`);

// Synchronous rename (no intent). The board card and the TopBar breadcrumb
// live-update from the resulting `task.updated` realtime event.
export const renameTask = (
  publicId: string,
  title: string,
): Promise<{ title: string }> =>
  api.patch<{ title: string }>(`/tasks/${publicId}`, { title });
